import React, { useMemo, useState } from "react";
import { splitSectionName } from "../projectsScheduleHelpers";

const ALL_SECTION_VALUE = "__all_section__";

const getSectionLabel = (section) => {
  const { code, title } = splitSectionName(section?.name);
  return title ? `${code} — ${title}` : code;
};

const WorkTypeRelationPicker = ({
  label,
  icon: Icon,
  selectedIds = [],
  onSelectedIdsChange,
  excludedIds = [],
  currentWorkTypeId,
  sections = [],
  getWorkTypeLabel,
  loading = false,
  disabled = false,
}) => {
  const [sectionId, setSectionId] = useState("");

  const selectedSection = useMemo(
    () => sections.find((section) => String(section.id) === sectionId),
    [sections, sectionId],
  );

  const availableWorkTypes = useMemo(() => {
    if (!selectedSection) return [];
    return (selectedSection.work_types || []).filter(
      (wt) =>
        wt.id !== currentWorkTypeId &&
        !selectedIds.includes(wt.id) &&
        !excludedIds.includes(wt.id),
    );
  }, [selectedSection, currentWorkTypeId, selectedIds, excludedIds]);

  const formatOptionLabel = (wt) =>
    wt.is_milestone ? `◆ ${wt.name}` : wt.name;

  const handleSectionChange = (e) => {
    setSectionId(e.target.value);
  };

  const addWorkTypeIds = (ids) => {
    const unique = ids.filter(
      (id) =>
        id !== currentWorkTypeId &&
        !selectedIds.includes(id) &&
        !excludedIds.includes(id),
    );
    if (unique.length === 0) return;
    onSelectedIdsChange([...selectedIds, ...unique]);
    setSectionId("");
  };

  const handleWorkTypeChange = (e) => {
    const value = e.target.value;
    if (!value) return;

    if (value === ALL_SECTION_VALUE) {
      addWorkTypeIds(availableWorkTypes.map((wt) => wt.id));
      return;
    }

    const id = Number(value);
    if (!Number.isFinite(id)) return;
    addWorkTypeIds([id]);
  };

  const removeId = (id) => {
    onSelectedIdsChange(selectedIds.filter((item) => item !== id));
  };

  const pickerDisabled =
    disabled || loading || sections.length === 0;

  return (
    <div className="edit-wt-drawer__relation">
      <div className="edit-wt-drawer__relation-label">
        {Icon ? <Icon /> : null}
        {label}
      </div>

      {selectedIds.length > 0 && (
        <div className="edit-wt-drawer__chips">
          {selectedIds.map((id) => (
            <span key={id} className="edit-wt-drawer__chip">
              {getWorkTypeLabel(id)}
              <button
                type="button"
                onClick={() => removeId(id)}
                disabled={disabled}
                aria-label="Удалить"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="edit-wt-drawer__relation-pickers">
        <select
          className="edit-wt-drawer__relation-select"
          value={sectionId}
          disabled={pickerDisabled}
          onChange={handleSectionChange}
        >
          <option value="">
            {loading ? "Загрузка разделов..." : "+ Выберите раздел"}
          </option>
          {sections.map((section) => (
            <option key={section.id} value={String(section.id)}>
              {getSectionLabel(section)}
            </option>
          ))}
        </select>

        <select
          className="edit-wt-drawer__relation-select"
          value=""
          disabled={pickerDisabled || !sectionId || availableWorkTypes.length === 0}
          onChange={handleWorkTypeChange}
        >
          <option value="">
            {!sectionId
              ? "Сначала выберите раздел"
              : availableWorkTypes.length === 0
                ? "Нет доступных работ/вех"
                : "+ Выберите работу или веху"}
          </option>
          {availableWorkTypes.length > 0 && (
            <option value={ALL_SECTION_VALUE}>Весь раздел</option>
          )}
          {availableWorkTypes.map((wt) => (
            <option key={wt.id} value={wt.id}>
              {formatOptionLabel(wt)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default WorkTypeRelationPicker;
